// Contract People card — the unified roster of named people on a managed
// contract. Owner + manager rows are required (exactly one of each); any
// other row is a free-text role label for an alert recipient. Inline
// editing on desktop; on viewport < md the row collapses into a drawer.
//
// Layout note: the dedicated Access column was removed because the panel
// that mounts this card (AccessPeoplePanel) renders a full ContractAccessCard
// immediately below — duplicating the per-contract ACL state in a column
// squeezed the Role/Name/Email inputs inside the slide-over drawer. The
// access-state badge + 'Grant access' inline action now live next to the
// Linked-user pill, which keeps the operational affordance reachable from
// the row without the extra column.
//
// The 6-column table is wrapped in an overflow-x-auto container so it can
// scroll horizontally at narrow panel widths rather than cramping every
// column. min-width is applied on the Name and Email <th>/<td> cells
// (browsers ignore min-width on <col>) so those inputs remain usable
// regardless of parent container width.
//
// Access state (rendered in the Linked-user cell) is one of:
//   - Granted          — emerald badge (sole emerald surface on this tab)
//   - All contracts    — slate badge (capability indicator, not a grant)
//   - Not granted      — muted slate badge + 'Grant access' inline action
//                        when the viewer canAdmin (action is a brand-link
//                        affordance, not a button, per brand-guide carve-out)
//   - External contact — muted slate badge (no userId set)
//
// Linked-user actions (also in the Linked-user cell):
//   - Unlinked row: a 'Link to user' button opens the LinkedUserPicker
//     inline; selecting a user atomically writes userId + name + email so
//     the row never settles in a half-linked state.
//   - Linked row: a 'Linked: {name}' pill plus an 'Unlink' affordance that
//     clears userId only — name/email are kept so the operator can edit
//     them into an external contact without re-typing.
//   - While linked, the row's Name + Email inputs are read-only so the
//     operator can't drift the linked org user's display data on this
//     contract. Mirrors PersonReferenceInput's linked-mode behaviour.
//
// The card itself uses a single-level `.card` surface (rounded-lg) and
// dense operational typography. No violet on rows. Manager alerts toggle
// is locked on with an accessible-by-default helper (sr-only text +
// aria-describedby) rather than a hover-only tooltip or inline caption.

import { useId, useState } from "react";
import { X } from "lucide-react";
import type { ContractPerson, OrganisationUser } from "../../types";
import { Card } from "../common";
import { clsx } from "clsx";
import LinkedUserPicker from "./LinkedUserPicker";

interface ContractPeopleCardProps {
  people: ContractPerson[];
  orgUsers: OrganisationUser[];
  /** userIds with an ACL grant on this contract. */
  accessUserIds: string[];
  canAdmin: boolean;
  onChange: (next: ContractPerson[]) => void;
  onRemove: (index: number) => void;
  /** Triggered from the inline 'Grant access' affordance on a Not-granted
   *  badge. Only rendered when the viewer canAdmin and the row's linked
   *  user is selected-access without a grant. */
  onGrantAccess?: (userId: string) => void;
}

type AccessState = "granted" | "all-contracts" | "not-granted" | "external";

function resolveAccessState(
  person: ContractPerson,
  orgUsers: OrganisationUser[],
  accessUserIds: string[],
): AccessState {
  if (!person.userId) return "external";
  const orgUser = orgUsers.find((u) => u.userId === person.userId);
  if (!orgUser) return "external";
  if (orgUser.role === "admin" || orgUser.role === "global-admin") return "all-contracts";
  if (orgUser.contractAccess === "all") return "all-contracts";
  return accessUserIds.includes(person.userId) ? "granted" : "not-granted";
}

const ACCESS_BADGE_CLASSES: Record<AccessState, string> = {
  // Emerald is reserved for this single surface — the access-state badge
  // in the People card's Linked-user cell — to keep its meaning ('has a
  // grant') unambiguous.
  granted:
    "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  // Capability, not a per-contract event — neutral slate.
  "all-contracts":
    "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  // Muted slate; an inline brand-link Grant-access affordance sits next to
  // this badge when the viewer canAdmin.
  "not-granted":
    "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  // Person without a userId — informational + alert-recipient only.
  external:
    "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
};

const ACCESS_BADGE_LABEL: Record<AccessState, string> = {
  granted: "Granted",
  "all-contracts": "All contracts",
  "not-granted": "Not granted",
  external: "External contact",
};

function AccessBadge({ state }: { state: AccessState }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-caption font-medium",
        ACCESS_BADGE_CLASSES[state],
      )}
    >
      {ACCESS_BADGE_LABEL[state]}
    </span>
  );
}

function roleLabel(role: string): string {
  if (role === "owner") return "Owner";
  if (role === "manager") return "Manager";
  return role;
}

export default function ContractPeopleCard({
  people,
  orgUsers,
  accessUserIds,
  canAdmin,
  onChange,
  onRemove,
  onGrantAccess,
}: ContractPeopleCardProps) {
  const managerHelperId = useId();
  // Which row (if any) currently has the inline LinkedUserPicker open. Local
  // UI state — keyed by row index so the operator can open one picker at a
  // time without dragging the open state into the parent's draft people array.
  const [pickerOpenIndex, setPickerOpenIndex] = useState<number | null>(null);
  const additionalPeople = people
    .map((p, i) => ({ person: p, index: i }))
    .filter(({ person }) => person.role !== "owner" && person.role !== "manager");

  const updatePerson = (index: number, patch: Partial<ContractPerson>) => {
    const next = people.map((p, i) => (i === index ? { ...p, ...patch } : p));
    onChange(next);
  };

  const handleLinkUser = (index: number, orgUser: OrganisationUser | undefined) => {
    if (!orgUser) {
      // Picker chip 'Clear' inside an open picker — close the picker and
      // leave the row untouched (the row was already unlinked).
      setPickerOpenIndex(null);
      return;
    }
    // Atomic hydrate: userId + name + email all land in the same onChange so
    // the row never settles in a half-linked state.
    updatePerson(index, {
      userId: orgUser.userId,
      name: orgUser.name,
      email: orgUser.email,
    });
    setPickerOpenIndex(null);
  };

  const handleUnlink = (index: number) => {
    // Drop the link but keep name + email so the operator can edit them
    // into an external contact without re-typing.
    updatePerson(index, { userId: undefined });
  };

  return (
    <Card padding="none">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-h3 text-slate-900 dark:text-slate-100">People</h3>
        <p className="text-caption text-slate-500 dark:text-slate-400">
          The contract owner and manager are required. Add additional alert
          recipients with free-text role labels.
        </p>
      </div>
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
        {/* colgroup: `width` is honoured on <col>; `min-width` is NOT — it is
            ignored by browsers. Fixed widths for compact columns only; the
            Name and Email minimum widths are on the <th>/<td> cells below. */}
        <colgroup>
          <col className="w-24" />
          <col />
          <col />
          <col className="w-20" />
          <col />
          <col className="w-10" />
        </colgroup>
        <thead className="bg-slate-50 dark:bg-slate-800">
          <tr>
            <th scope="col" className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Role
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Name
            </th>
            <th scope="col" className="min-w-[200px] px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Email
            </th>
            <th scope="col" className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Alerts
            </th>
            <th scope="col" className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Linked user
            </th>
            <th scope="col" className="px-4 py-2 text-right text-overline uppercase text-slate-500 dark:text-slate-400">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {people.map((person, index) => {
            const isOwner = person.role === "owner";
            const isManager = person.role === "manager";
            const isCanonical = isOwner || isManager;
            const accessState = resolveAccessState(person, orgUsers, accessUserIds);
            const linkedUser = person.userId
              ? orgUsers.find((u) => u.userId === person.userId)
              : undefined;
            const isLinked = !!person.userId;
            const personLabel = `${person.role} ${person.name || "(unnamed)"}`;
            const pickerOpen = pickerOpenIndex === index;

            return (
              // Row key must be stable across role/name mutations — keying
              // by a value the user edits (e.g. `${person.role}-${index}`)
              // unmounts and remounts the row on every keystroke and kills
              // input focus. Array order is the stable identity here:
              // additional people don't have client-side ids, and add/remove
              // happens at row granularity (not in-place reorders), so the
              // index is the right key.
              <tr key={index}>
                <td className="px-4 py-3 text-body-sm text-slate-700 dark:text-slate-300">
                  {isCanonical ? (
                    <span className="capitalize">{roleLabel(person.role)}</span>
                  ) : (
                    <input
                      type="text"
                      maxLength={60}
                      value={person.role}
                      aria-label={`Role for ${person.name || "(unnamed)"}`}
                      onChange={(e) => updatePerson(index, { role: e.target.value })}
                      className="input"
                    />
                  )}
                </td>
                <td className="min-w-[160px] px-4 py-3 text-body-sm">
                  <input
                    type="text"
                    value={person.name}
                    aria-label={`Name for ${person.role} ${person.name || "(unnamed)"}`}
                    onChange={(e) => updatePerson(index, { name: e.target.value })}
                    readOnly={isLinked}
                    className={clsx(
                      "input",
                      isLinked && "bg-slate-50 dark:bg-slate-800 cursor-not-allowed",
                    )}
                  />
                </td>
                <td className="min-w-[200px] px-4 py-3 text-body-sm">
                  <input
                    type="email"
                    value={person.email ?? ""}
                    aria-label={`Email for ${personLabel}`}
                    onChange={(e) =>
                      updatePerson(index, { email: e.target.value || undefined })
                    }
                    readOnly={isLinked}
                    // When the input is read-only the full address is accessible
                    // on hover — useful if a long email truncates inside the cell.
                    title={isLinked ? (person.email ?? "") : undefined}
                    className={clsx(
                      "input",
                      isLinked && "bg-slate-50 dark:bg-slate-800 cursor-not-allowed",
                    )}
                  />
                </td>
                <td className="px-4 py-3 text-body-sm">
                  {isManager ? (
                    <>
                      <button
                        type="button"
                        role="switch"
                        aria-checked="true"
                        aria-disabled="true"
                        aria-label="Manager alerts (locked on)"
                        aria-describedby={managerHelperId}
                        title="Manager alerts are always on."
                        className="inline-flex items-center gap-2 cursor-not-allowed opacity-80"
                      >
                        <span className="inline-block w-9 h-5 bg-brand-600 rounded-full relative">
                          <span className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
                        </span>
                        <span className="text-caption text-slate-500 dark:text-slate-400">On</span>
                      </button>
                      {/* Visually hidden but kept in the DOM so screen-reader
                          users still get the locked-on explanation via the
                          switch's aria-describedby. Sighted users get the
                          same wording via the title tooltip. */}
                      <p id={managerHelperId} className="sr-only">
                        Manager alerts are always on.
                      </p>
                    </>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={person.alertsEnabled}
                      aria-label={isOwner ? "Owner alerts" : `Alerts for ${personLabel}`}
                      onClick={() =>
                        updatePerson(index, { alertsEnabled: !person.alertsEnabled })
                      }
                      className="inline-flex items-center gap-2"
                    >
                      <span
                        className={clsx(
                          "inline-block w-9 h-5 rounded-full relative transition-colors",
                          person.alertsEnabled
                            ? "bg-brand-600"
                            : "bg-slate-300 dark:bg-slate-600",
                        )}
                      >
                        <span
                          className={clsx(
                            "absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all",
                            person.alertsEnabled ? "right-0.5" : "left-0.5",
                          )}
                        />
                      </span>
                      <span className="text-caption text-slate-500 dark:text-slate-400">
                        {person.alertsEnabled ? "On" : "Off"}
                      </span>
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-body-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    {linkedUser ? (
                      <>
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-caption text-slate-700 dark:text-slate-200">
                          Linked: {linkedUser.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUnlink(index)}
                          aria-label={`Unlink ${linkedUser.name} from ${personLabel}`}
                          className="text-caption font-medium text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
                        >
                          Unlink
                        </button>
                        <AccessBadge state={accessState} />
                        {accessState === "not-granted" && canAdmin && onGrantAccess && person.userId && (
                          <button
                            type="button"
                            onClick={() => onGrantAccess(person.userId!)}
                            className="text-caption font-medium text-brand-600 dark:text-brand-400 hover:underline focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
                          >
                            Grant access
                          </button>
                        )}
                      </>
                    ) : pickerOpen ? (
                      <div className="w-full min-w-0">
                        <LinkedUserPicker
                          users={orgUsers}
                          onSelect={(u) => handleLinkUser(index, u)}
                          label={personLabel}
                        />
                      </div>
                    ) : (
                      <>
                        <span className="text-caption text-slate-400 dark:text-slate-500">
                          Not linked
                        </span>
                        <button
                          type="button"
                          onClick={() => setPickerOpenIndex(index)}
                          className="text-caption font-medium text-brand-600 dark:text-brand-400 hover:underline focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
                        >
                          Link to user
                        </button>
                        <AccessBadge state={accessState} />
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  {!isCanonical && (
                    <button
                      type="button"
                      aria-label={`Remove ${person.name || personLabel}`}
                      onClick={() => onRemove(index)}
                      className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {additionalPeople.length === 0 && (
        <p className="px-4 py-6 text-center text-body-sm text-slate-500 dark:text-slate-400">
          No additional people on this contract. Add a project manager or other
          stakeholder using the button above.
        </p>
      )}
    </Card>
  );
}
